import { forwardRef, type HTMLAttributes } from "react";
import { cn } from "@/lib/cn";

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Opt-in hover-lift for interactive list cards (clickable rows of cards).
   * Static content cards leave this off — the Mixpanel look is flat.
   */
  hoverable?: boolean;
}

/**
 * Card + sub-components — shared IQ-family house style, the flat "Mixpanel"
 * card: `rounded-2xl` + the soft `shadow-mx` (no 1px ring, no hover-lift),
 * matching the ads-iq dashboard / Performance cards. List cards that are
 * themselves clickable can opt back into the lift via `hoverable`.
 */
export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, hoverable = false, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "rounded-2xl bg-bg-card shadow-mx",
        hoverable &&
          "transition-all hover:shadow-card-hover hover:-translate-y-0.5 cursor-pointer",
        className,
      )}
      {...props}
    />
  ),
);
Card.displayName = "Card";

export const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("px-5 pt-4 pb-3 border-b border-border-lighter", className)}
      {...props}
    />
  ),
);
CardHeader.displayName = "CardHeader";

export const CardTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn(
        "text-base font-semibold text-text-primary tracking-tight",
        className,
      )}
      {...props}
    />
  ),
);
CardTitle.displayName = "CardTitle";

export const CardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("p-5", className)} {...props} />
  ),
);
CardContent.displayName = "CardContent";

export const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("px-5 py-3 border-t border-border-lighter", className)}
      {...props}
    />
  ),
);
CardFooter.displayName = "CardFooter";
